/**
 * @file Haupt-Skript für Website-Interaktivität und Animationen.
 * @description Bündelt alle jQuery-basierten Funktionalitäten wie Animationen beim Scrollen,
 * Slider-Initialisierungen, Videosteuerung und die vereinheitlichte MixItUp-Filterung.
 * Die Struktur ist modular aufgebaut, um die Wartbarkeit zu verbessern und
 * den globalen Namespace sauber zu halten.
 * @requires jQuery
 * @requires slick-carousel
 * @requires mixitup
 */

(function($) {
    'use strict';

    /**
     * @function createStore
     * @description Liste von Fahrzeug-IDs im localStorage, z.B. Merkliste (später auch Vergleich).
     * Format: { v: 1, items: [{ id: 904, added: 1759740000000 }] }, neueste zuletzt.
     * Jede Änderung (auch aus einem anderen Tab) löst auf document das Event store.event aus.
     * Ohne Speicher (privater Modus, gesperrte Website-Daten) ist store.available false.
     * @param {string} key localStorage-Key
     * @param {Object} [options] max: maximale Anzahl (0 = unbegrenzt)
     */
    const createStore = function(key, options) {
        const max = (options && options.max) || 0;
        const event = key + ':change';

        const available = (() => {
            try {
                localStorage.setItem(key + ':test', '1');
                localStorage.removeItem(key + ':test');
                return true;
            } catch (e) {
                return false;
            }
        })();

        const read = () => {
            try {
                const data = JSON.parse(localStorage.getItem(key));
                return data && data.v === 1 && Array.isArray(data.items) ? data.items : [];
            } catch (e) {
                return [];
            }
        };

        const write = (items) => {
            try {
                localStorage.setItem(key, JSON.stringify({ v: 1, items: items }));
            } catch (e) {
                return false;
            }
            $(document).trigger(event);
            return true;
        };

        const ids = () => read().map((item) => item.id);
        const has = (id) => ids().includes(parseInt(id, 10));

        const add = (id) => {
            id = parseInt(id, 10);
            if (!id || has(id) || (max && ids().length >= max)) return false;
            return write(read().concat({ id: id, added: Date.now() }));
        };

        const remove = (id) => {
            id = parseInt(id, 10);
            return write(read().filter((item) => item.id !== id));
        };

        // nur die IDs behalten, die es noch gibt (verkauft, PDF entfernt)
        const prune = (validIds) => {
            const valid = validIds.map((id) => parseInt(id, 10));
            const items = read();
            const kept = items.filter((item) => valid.includes(item.id));
            if (kept.length !== items.length) write(kept);
        };

        if (available) {
            $(window).on('storage', (e) => {
                if (e.originalEvent.key === key || e.originalEvent.key === null) $(document).trigger(event);
            });
        }

        return {
            available: available,
            event: event,
            ids: ids,
            has: has,
            add: add,
            remove: remove,
            toggle: (id) => (has(id) ? remove(id) : add(id)),
            clear: () => write([]),
            prune: prune,
        };
    };

    /**
     * @namespace Site
     * @description Haupt-Namespace für die gesamte Anwendungslogik.
     */
    const Site = {

        /**
         * @method init
         * @description Initialisiert alle Module und bindet die globalen Event-Listener.
         */
        init: function() {
            this.helpers.initExternalLinks();
            this.pdfViewer.init();
            this.favorites.init();
            this.compare.init();
            this.navigation.init();
            this.sliders.init();
            this.video.init();
            this.filters.init();
            this.effects.init();
            this.layout.init(); // Hinzugefügt
            this.bindGlobalEvents();
        },

        /**
         * @method bindGlobalEvents
         * @description Bündelt rechenintensive Events, um die Performance zu optimieren.
         */
        bindGlobalEvents: function() {
            let ticking = false;

            const onScrollResize = () => {
                if (!ticking) {
                    window.requestAnimationFrame(() => {
                        this.effects.animateInOnScroll();
                        this.navigation.updateHeaderBackground();
                        this.effects.updateScrollTextColor();
                        this.effects.updateParallax();
                        this.effects.updateMarqueeScroll();
                        this.layout.adjustFooterPadding(); // Hinzugefügt
                        ticking = false;
                    });
                    ticking = true;
                }
            };

            $(window).on('scroll load resize', onScrollResize);
            // main.min.js is loaded async, so the load event may already be over
            onScrollResize();
            if (document.fonts) document.fonts.ready.then(onScrollResize);
            $(window).on('hashchange load', this.filters.handleBrandHash);
        },

        //======================================================================
        // MODULE: LAYOUT (NEU)
        //======================================================================
        layout: {
            init: function() {
                this.adjustFooterPadding();
            },
            /**
             * @description Prüft, ob '.js-footernote' existiert und passt das
             * padding-bottom des Bodys entsprechend der Höhe des Elements an.
             * Die Höhe steht zusätzlich als CSS-Variable --footerNoteHeight bereit
             * (z. B. für Module in Bildschirmhöhe wie heroSpecial).
             */
            adjustFooterPadding: function() {
                const $footerNote = $('.js-footernote');
                if ($footerNote.length) {
                    const footerNoteHeight = $footerNote.outerHeight();
                    if (footerNoteHeight === this.footerNoteHeight) return;
                    this.footerNoteHeight = footerNoteHeight;
                    $('body').css('padding-bottom', footerNoteHeight + 'px');
                    document.documentElement.style.setProperty('--footerNoteHeight', footerNoteHeight + 'px');
                }
            }
        },

        //======================================================================
        // MODULE: NAVIGATION
        //======================================================================
        navigation: {
            init: function() {
                this.initNavButton();
            },
            initNavButton: function() {
                $(".js-navbutton").on("click", function() {
                    $(".js-header").toggleClass("navOpen");
                    $(".js-navbutton").toggleClass("active");
                    $(".js-navmenu").toggleClass("active");
                    $("body").toggleClass("freeze");
                });
            },
            updateHeaderBackground: function() {
                const isScrolled = $(window).scrollTop() >= 40;
                $(".js-header").toggleClass("background", isScrolled);
            }
        },

        //======================================================================
        // MODULE: SLIDERS (Slick Carousel)
        //======================================================================
        sliders: {
            init: function() {
                this.initSliderTeaser();
                this.initSliderDetails();
                this.initSliderCard();
                this.initTimeline();
                this.initHeroSpecial();
            },
            initSliderTeaser: function() {
                $(".js-sliderteaser").each(function() {
                    const $module = $(this);
                    const $slider = $module.find(".js-sliderteaser-slider");
                    const $nav = $module.find(".js-sliderteaser-nav");
                    if (!$slider.length) return;
                    $slider.slick({
                        infinite: true,
                        arrows: false,
                        autoplay: true,
                        autoplaySpeed: 2500,
                        speed: 1000,
                        centerMode: true,
                        variableWidth: true,
                        focusOnSelect: true,
                        swipeToSlide: true,
                        touchThreshold: 20,
                    });
                    $slider.on('init reInit beforeChange', (event, slick, currentSlide, nextSlide) => {
                        const slideIndex = nextSlide ?? currentSlide ?? 0;
                        const $currentSlideEl = $(slick.$slides.get(slideIndex));
                        const index = $currentSlideEl.find(".js-sliderteaser-slide").data("index");
                        $nav.find("li").removeClass("active").eq(index - 1).addClass("active");
                    });
                    $nav.find("li").on("click", function() {
                        $slider.slick('slickGoTo', $(this).index());
                    });
                });
            },
            initSliderDetails: function() {
                $(".js-sliderdetails").each(function() {
                    const $module = $(this);
                    $module.find(".js-sliderdetails-slider").slick({
                        infinite: true,
                        arrows: true,
                        autoplay: true,
                        autoplaySpeed: 2500,
                        speed: 1000,
                        focusOnSelect: true,
                        centerMode: true,
                        variableWidth: true,
                        prevArrow: $module.find(".js-sliderdetails-prev"),
                        nextArrow: $module.find(".js-sliderdetails-next"),
                    });
                });
            },
            initSliderCard: function() {
                var $slider = $(".js-slidercard");
                var initialSettings = {
                    infinite: true,
                    arrows: false,
                    autoplay: true,
                    autoplaySpeed: 0,
                    speed: 10000,
                    cssEase: 'linear',
                    waitForAnimate: false,
                    centerMode: true,
                    variableWidth: true,
                    pauseOnHover: true,
                    touchMove: true,
                    swipe: true,
                    responsive: [{
                        breakpoint: 700,
                        settings: {
                            speed: 5000
                        }
                    }]
                };
                $slider.slick(initialSettings);
                $slider.one('swipe', function() {
                    if (window.innerWidth < 700) {
                        var $this = $(this);
                        var newSettings = {
                            infinite: true,
                            arrows: false,
                            autoplay: false,
                            speed: 500,
                            cssEase: 'ease',
                            waitForAnimate: false,
                            centerMode: true,
                            variableWidth: true,
                            touchMove: true,
                            swipe: true
                        };
                        $this.slick('unslick').slick(newSettings);
                    }
                });
            },
            initTimeline: function() {
                $(".js-timeline").each(function() {
                    const $module = $(this);
                    $module.find(".js-timeline-slider").slick({
                        infinite: false,
                        arrows: true,
                        speed: 500,
                        slidesToScroll: 3,
                        waitForAnimate: true,
                        focusOnSelect: true,
                        variableWidth: true,
                        prevArrow: $module.find(".js-timeline-prev"),
                        nextArrow: $module.find(".js-timeline-next"),
                        responsive: [{ breakpoint: 700, settings: { adaptiveHeight: true, slidesToScroll: 1, } }]
                    });
                });
            },
            initHeroSpecial: function() {
                $(".js-herospecial").each(function() {
                    const $module = $(this);
                    const $slider = $module.find(".js-herospecial-slider");
                    const $videos = $slider.find(".js-herospecial-video");
                    if (!$slider.length) return;

                    // Only the video visible at the current breakpoint (desktop or mobile) is played
                    const playVideos = function($slide) {
                        $slide.find(".js-herospecial-video").filter(":visible").each(function() {
                            const playPromise = this.play();
                            if (playPromise) playPromise.catch(() => {});
                        });
                    };
                    const pauseVideos = function($except) {
                        $videos.not($except.find(".js-herospecial-video").filter(":visible")).each(function() {
                            this.pause();
                        });
                    };

                    $videos.on("playing", function() {
                        $(this).addClass("is-playing");
                    });

                    // Autoplay is driven by the progress bar in the active dot, so bar and slide change stay in sync
                    const slideDuration = 7000; // ms per slide incl. fade
                    const startProgress = function(index) {
                        const $dots = $module.find(".heroSpecial__dots li").removeClass("is-progress");
                        const dot = $dots.get(index);
                        if (!dot) return;
                        void dot.offsetWidth; // restart the CSS animation
                        $(dot).addClass("is-progress");
                    };
                    $module.get(0).style.setProperty("--heroSpecialDuration", slideDuration + "ms");
                    $module.on("animationend", ".heroSpecial__dots li", (event) => {
                        if (event.originalEvent.animationName === "heroSpecialProgress") {
                            $slider.slick("slickNext");
                        }
                    });
                    // Pause the progress (and with it the autoplay) while the tab is hidden
                    document.addEventListener("visibilitychange", () => {
                        $module.toggleClass("is-paused", document.hidden);
                    });

                    $slider.on("init", (event, slick) => {
                        playVideos($(slick.$slides.get(slick.currentSlide)));
                        startProgress(slick.currentSlide);
                    });
                    $slider.on("beforeChange", (event, slick, currentSlide, nextSlide) => {
                        playVideos($(slick.$slides.get(nextSlide)));
                        startProgress(nextSlide);
                    });
                    $slider.on("afterChange", (event, slick, currentSlide) => {
                        pauseVideos($(slick.$slides.get(currentSlide)));
                    });

                    $slider.slick({
                        rows: 0,
                        fade: true,
                        infinite: true,
                        arrows: false,
                        dots: true,
                        dotsClass: "heroSpecial__dots",
                        speed: 1000,
                    });

                    // Switch between desktop and mobile video when crossing the breakpoint
                    window.matchMedia("(max-width: 700px)").addEventListener("change", () => {
                        const $current = $slider.find(".slick-current");
                        pauseVideos($current);
                        playVideos($current);
                    });
                });
            }
        },

        //======================================================================
        // MODULE: FILTERS (MixItUp) - UNIFIED & CORRECTED VERSION
        //======================================================================
        filters: {
            init: function() {
                this.initAdvancedFilters();
                this.initSimpleFilter();
                this.applyInitialFilterFromURL();
            },

            applyInitialFilterFromURL: function() {
                const pathSegments = window.location.pathname.split('/').filter(Boolean);
                let filterDimension = '';
                let filterValue = '';

                if (pathSegments[0] === 'marken' && pathSegments.length > 1) {
                    filterDimension = 'marke';
                    filterValue = pathSegments[1];
                } else if (pathSegments[0] === 'standorte' && pathSegments.length > 1) {
                    filterDimension = 'standort';
                    filterValue = pathSegments[1];
                }

                if (filterDimension && filterValue) {
                    const $select = $(`.js-filter-select[data-filter-dimension="${filterDimension}"]`);
                    if ($select.length && $select.find(`option[value="${filterValue}"]`).length) {
                        $select.val(filterValue).trigger('change');
                    }
                }
            },

            initAdvancedFilters: function() {
                $('.js-advanced-filter-module').each(function() {
                    const $filterModule = $(this);
                    const targetSelectors = $filterModule.data('target').split(',').map(s => s.trim());
                    if (!targetSelectors.length) return;

                    // Optional: nur diese Ziele fließen in den Zähler ein (z.B. nur Autos, nicht die Ansprechpartner).
                    // Ohne data-count-target werden alle Ziele gezählt.
                    const countSelectors = String($filterModule.data('count-target') || '').split(',').map(s => s.trim()).filter(Boolean);

                    const mixers = [];
                    const countMixers = [];
                    let $containers = $();

                    // GEÄNDERT: Findet jetzt <select> UND <ul> mit der Klasse .js-filter-select
                    const $filterSelects = $filterModule.find('.js-filter-select')
                        .add($('[data-global-filter="true"]'));
                    const $resetButton = $filterModule.find('.js-filter-reset');
                    const $countElement = $filterModule.find('.js-filter-count');
                    const $locationSelect = $filterModule.find('.js-location-select');
                    const $pins = $filterModule.find('.js-location-pin');
                    
                    $filterSelects.each(function() {
                    const $select = $(this);
                    const classList = ($select.attr('class') || '').split(/\s+/);

                    $.each(classList, function(index, className) {
                        if (className.startsWith('js-preselect-')) {
                            const preselectValue = className.replace('js-preselect-', '');
                            if (!preselectValue) return;

                            // Warte kurz, bis alle Elemente geladen sind
                            setTimeout(function() {
                                if ($select.is('select')) {
                                    // Für <select> den passenden Wert setzen und change auslösen
                                    const $option = $select.find(`option[value="${preselectValue}"], option[data-select="${preselectValue}"]`);
                                    if ($option.length) {
                                        $select.val($option.val()).trigger('change');
                                    }
                                } else if ($select.is('ul')) {
                                    // Für <ul> das passende <li> auswählen
                                    const $li = $select.find(`li[value="${preselectValue}"], li[data-select="${preselectValue}"]`);
                                    if ($li.length) {
                                        $select.find('li').removeAttr('selected');
                                        $li.attr('selected', 'selected');
                                        $select.trigger('change');
                                    }
                                }
                            }, 500);

                            return false; // Schleife verlassen, nur erster Preselect pro Filter
                        }
                    });
                });


                // NEU: Helferfunktion, um den Wert von <select> oder <ul> zu bekommen
                const getFilterValue = ($el) => {
                    if ($el.is('select')) {
                        // prop statt val: jQuery.val() liefert null, wenn die gewählte Option disabled ist (z.B. Marke per URL ohne Treffer)
                        return $el.prop('value');
                    }
                    if ($el.is('ul')) {
                        return $el.find('li[selected]').attr('value') || 'all';
                    }
                    return $el.attr('value');
                };

                // NEU: Event-Brücke, damit ULs bei Klick ein 'change'-Event auslösen
                $filterSelects.filter('ul').each(function() {
                    const $ul = $(this);
                    $ul.on('click', 'li:not([disabled])', function() {
                        const $li = $(this);
                        if ($li.attr('selected')) return; // Bereits ausgewählt

                        $ul.find('li').removeAttr('selected');
                        $li.attr('selected', 'selected');
                        $ul.trigger('change');
                    });
                });

                const updateAvailableOptions = () => {
                    // Zählt über den Mixer-Status, damit auch Filter anderer Module auf demselben Container
                    // (z.B. vorselektierte Abteilung) und .is-hidden-by-default berücksichtigt werden
                    const countShow = countMixers.reduce((sum, mixer) => sum + mixer.getState().totalShow, 0);

                    if ($countElement.length) {
                        $countElement.text(countShow);
                    }

                    // GEÄNDERT: Nutzt die Helferfunktion
                    const markeFilterAktiv = getFilterValue($filterSelects.filter('[data-filter-dimension="marke"]')) !== 'all';

                    if (markeFilterAktiv && countShow === 0) {
                        // GEÄNDERT: Behandelt das Deaktivieren für beide Elementtypen
                        if ($locationSelect.is('select')) {
                            $locationSelect.prop('disabled', true);
                            $locationSelect.find('option').not('[value="all"]').prop('disabled', true);
                        } else { // Es ist ein <ul>
                            $locationSelect.addClass('disabled');
                            $locationSelect.find('li').not('[value="all"]').attr('disabled', 'disabled');
                        }
                        $pins.addClass('disabled');
                        return;
                    }
                    
                    if ($locationSelect.is('select')) {
                        $locationSelect.prop('disabled', false);
                    } else {
                        $locationSelect.removeClass('disabled');
                    }

                    const allItems = $containers.find('.mix');
                    
                    $filterSelects.each(function() {
                        const $currentSelect = $(this);
                        const currentDimension = $currentSelect.data('filter-dimension');
                        const otherFiltersSelector = $filterSelects.not($currentSelect).map(function() {
                            // GEÄNDERT: Nutzt die Helferfunktion
                            const value = getFilterValue($(this));
                            return (value && value !== 'all') ? `[data-${$(this).data('filter-dimension')}~="${value}"]` : '';
                        }).get().join('');

                        const availableOptions = new Set();
                        const potentialMatches = otherFiltersSelector ? allItems.filter(otherFiltersSelector) : allItems;
                        
                        potentialMatches.each(function() {
                            const values = String($(this).data(currentDimension) || '').split(' ').filter(Boolean);
                            values.forEach(value => availableOptions.add(value));
                        });
                        
                        // GEÄNDERT: Behandelt sowohl <option> als auch <li>
                        $currentSelect.find('option, li').not('[value="all"]').each(function() {
                            const $item = $(this);
                            const itemValue = $item.is('option') ? $item.val() : $item.attr('value');
                            const isDisabled = !availableOptions.has(itemValue);
                            
                            if ($item.is('option')) {
                                $item.prop('disabled', isDisabled);
                            } else { // Es ist ein <li>
                                $item.attr('disabled', isDisabled ? 'disabled' : null);
                            }
                        });

                        if (currentDimension === 'standort') {
                            $pins.each(function() {
                                const $pin = $(this);
                                $(this).toggleClass('disabled', !availableOptions.has($pin.data('pin')));
                            });
                        }
                    });
                };

                    targetSelectors.forEach(selector => {
                        const $container = $(selector);
                        if (!$container.length) return;
                        
                        $containers = $containers.add($container);

                        const initialFilterSelector = '.mix:not(.is-hidden-by-default)';

                        const mixer = mixitup($container[0], {
                            selectors: { target: '.mix' },
                            load: { filter: initialFilterSelector },
                            animation: { duration: 300 },
                            callbacks: {
                                onMixEnd: updateAvailableOptions
                            }
                        });
                        
                        if(mixer) {
                            mixers.push(mixer);
                            if (!countSelectors.length || countSelectors.includes(selector)) {
                                countMixers.push(mixer);
                            }
                        }
                    });

                    if (!mixers.length) return;

                    // GEÄNDERT: Logik für Pin-Auswahl flexibel gemacht
                    $locationSelect.on('change', function() {
                        const $this = $(this);
                        let selectedPin;
                        if ($this.is('select')) {
                            selectedPin = $this.find(':selected').data('select');
                        } else { // Es ist ein <ul>
                            selectedPin = $this.find('li[selected]').data('select');
                        }
                        $pins.removeClass('active');
                        if(selectedPin) {
                            $pins.filter(`[data-pin="${selectedPin}"]`).addClass('active');
                        }
                    });

                    // GEÄNDERT: Logik für Pin-Klick flexibel gemacht
                    $pins.on('click', function(e) {
                        e.preventDefault();
                        if ($(this).hasClass('disabled')) return;
                        
                        const $pin = $(this);
                        const pinValue = $pin.data('pin');
                        
                        if ($pin.hasClass('active')) {
                            if ($locationSelect.is('select')) {
                                $locationSelect.val('all').trigger('change');
                            } else {
                                $locationSelect.find('li').removeAttr('selected');
                                $locationSelect.find('li[value="all"]').attr('selected', 'selected');
                                $locationSelect.trigger('change');
                            }
                        } else {
                            if ($locationSelect.is('select')) {
                                const matchedOption = $locationSelect.find(`option[data-select="${pinValue}"]`);
                                $locationSelect.val(matchedOption.val() || 'all').trigger('change');
                            } else {
                                const matchedLi = $locationSelect.find(`li[data-select="${pinValue}"]`);
                                $locationSelect.find('li').removeAttr('selected');
                                if (matchedLi.length) {
                                    matchedLi.attr('selected', 'selected');
                                } else {
                                    $locationSelect.find('li[value="all"]').attr('selected', 'selected');
                                }
                                $locationSelect.trigger('change');
                            }
                        }
                    });

                    const applyFilters = () => {
                        mixers.forEach((mixer, i) => {
                            const $container = $containers.eq(i);

                            const relevantModules = $('.js-advanced-filter-module').filter(function() {
                                const targets = ($(this).data('target') || '').split(',').map(s => s.trim());
                                return targets.includes(`#${$container.attr('id')}`) || targets.some(sel => sel === $container.selector);
                            });

                            const combinedFilters = relevantModules.find('.js-filter-select').map(function() {
                                const value = getFilterValue($(this));
                                return (value && value !== 'all') ? `[data-${$(this).data('filter-dimension')}~="${value}"]` : null;
                            }).get().filter(Boolean);

                            // Abteilungsfilter speziell behandeln
                            const abteilungsFilter = $filterSelects.filter('[data-filter-dimension="abteilung"]');
                            const abteilungValue = getFilterValue(abteilungsFilter);

                            // if (abteilungValue === 'all') {
                                // 'all' für Abteilung: hidden-by-default Elemente ausblenden
                                const filterString = combinedFilters.length > 0 
                                    ? combinedFilters.join('') 
                                    : ':not(.is-hidden-by-default)';
                                mixer.filter(filterString);
                            // } else {
                            //     mixer.filter(combinedFilters.length > 0 ? combinedFilters.join('') : 'all');
                            // }
                        });
                    };


                    
                    updateAvailableOptions();

                    $filterSelects.on('change', () => setTimeout(applyFilters, 0));

                    // "Filter löschen" erst zeigen, wenn mindestens ein Filter gesetzt ist
                    const updateResetButton = () => {
                        const hasActiveFilter = $filterSelects.get().some(select => getFilterValue($(select)) !== 'all');
                        $resetButton.toggleClass('is-hidden', !hasActiveFilter);
                    };
                    updateResetButton();
                    $filterSelects.on('change', updateResetButton);

                    // GEÄNDERT: Reset-Logik für beide Filtertypen
                    $resetButton.on('click', () => {
                        $filterSelects.each(function() {
                            const $select = $(this);
                            if ($select.is('select')) {
                                $select.val('all');
                            } else if ($select.is('ul')) {
                                $select.find('li').removeAttr('selected');
                                $select.find('li[value="all"]').attr('selected', 'selected');
                            }
                        }).trigger('change'); // Löst für alle Filter das change-Event aus
                        $pins.removeClass('active');
                    });
                });
            },


            initSimpleFilter: function() {
                $('.js-mixitup-module[data-module-name="press"], .js-mixitup-module[data-module-name="jobs"]').each(function() {
                    const $module = $(this);
                    const $container = $module.find('.js-mixitup-container');
                    const $selectFilter = $module.find('.js-filter-select');
                    const $resetButton = $module.find('.js-filter-reset');
                    const $countElement = $module.find('.js-filter-count');
                    const mixer = Site.filters.createMixer($container, $countElement, false);
                    if (!mixer) return;
                    // "Filter löschen" erst zeigen, wenn ein Filter gesetzt ist
                    const updateResetButton = () => $resetButton.toggleClass('is-hidden', $selectFilter.val() === 'all');
                    updateResetButton();
                    $selectFilter.on('change', function() {
                        const selectedValue = $(this).val();
                        // Erstelle den korrekten CSS-Klassenselektor, außer wenn "Alle Standorte" gewählt ist.
                        // Presse-Optionen enthalten den Punkt bereits (".js-filter-1"), daher nicht doppelt voranstellen
                        const filterSelector = selectedValue === 'all' ? 'all' : '.' + selectedValue.replace(/^\./, '');
                        mixer.filter(filterSelector);
                        updateResetButton();
                    });
                    $resetButton.on('click', () => {
                        mixer.filter('all');
                        $selectFilter.val('all');
                        updateResetButton();
                    });
                });
            },

            createMixer: function($container, $countElement, isMultifilter = false) {
                if (!$container.length) return null;
                const isContactsModule = $container.find('.contactPerson__item').length > 0;
                let initialFilter = isContactsModule ? '.mix:not(.is-hidden-by-default)' : 'all';
                const mixer = mixitup($container[0], {
                    selectors: { target: '.mix' },
                    animation: { duration: 300, effects: 'fade scale(0.9)' },
                    multifilter: { enable: isMultifilter },
                    load: { filter: initialFilter },
                    callbacks: {
                        onMixEnd: (state) => {
                            const isResetState = state.activeFilter.selector === 'all' || state.activeFilter.selector === '.mix:not(.is-hidden-by-default)';
                            if (isContactsModule && isResetState) {
                                const totalItems = $container.find('.mix').length;
                                $countElement.text(totalItems);
                            } else {
                                $countElement.text(state.totalShow);
                            }
                        }
                    }
                });
                if (isContactsModule) {
                    const totalItems = $container.find('.mix').length;
                    $countElement.text(totalItems);
                } else {
                    $countElement.text(mixer.getState().totalShow);
                }
                return mixer;
            },

            handleBrandHash: function() {
                const hash = window.location.hash;
                if (!hash.startsWith("#doerr-brand-")) return;
                const value = hash.replace("#doerr-brand-", "");
                const $select = $(".form__field:first-child .js-filter-select");
                if ($select.length) {
                    $("html, body").animate({
                        scrollTop: $select.offset().top - 100
                    }, 500);
                    $select.val(value).trigger("change");
                }
            }
        },

        //======================================================================
        // MODULE: INTERACTIVE EFFECTS
        //======================================================================
        effects: {
            parallaxState: new WeakMap(),
            animationFrameId: null,

            init: function() {
                this.initScrollText();
                this.startAnimationLoop();
            },

            startAnimationLoop: function() {
                const loop = () => {
                    this.applyParallax();
                    this.animationFrameId = window.requestAnimationFrame(loop);
                };
                this.animationFrameId = window.requestAnimationFrame(loop);
            },

            animateInOnScroll: function() {
                const offset = 80;
                const viewPortStart = $(window).scrollTop();
                const viewPortEnd = viewPortStart + $(window).height();
                $(".animateIn:visible").each(function() {
                    const $el = $(this);
                    const elementTop = $el.offset().top;
                    const elementBottom = elementTop + $el.outerHeight();
                    const isInView = (elementTop + offset) <= viewPortEnd && (elementBottom - offset) >= viewPortStart;
                    if (isInView) {
                        const delay = $el.data("animation-delay") || 0;
                        $el.css("transition-delay", `${delay}s`).addClass("animateIn--active");
                    } else {
                        $el.removeClass("animateIn--active");
                    }
                });
            },

            initScrollText: function() {
                $('.js-scrolltext').each(function() {
                    const $el = $(this);
                    const words = $el.text().trim().split(" ");
                    $el.empty();
                    words.forEach(word => {
                        $el.append($('<span>').text(word + ' '));
                    });
                });
            },

            updateScrollTextColor: function() {
                $('.js-scrolltext').each(function() {
                    const $el = $(this);
                    const $words = $el.find("span");
                    if ($words.length === 0) return;
                    const elTop = $el.offset().top;
                    const scrollTop = $(window).scrollTop();
                    const windowHeight = $(window).height();
                    const start = scrollTop + windowHeight * 0.9;
                    const end = scrollTop + windowHeight * 0.1;
                    let progress = Math.max(0, Math.min(1, (start - elTop) / (start - end)));
                    const activeCount = Math.floor(progress * $words.length);
                    $words.removeClass("active").slice(0, activeCount).addClass("active");
                });
            },
            
            updateParallax: function() {
                const isMobile = $(window).width() <= 700;
                const parallaxElements = $("[data-parallax-y], [data-parallax-x]").not(".marquee__line");

                // Der generelle "return" für mobile Ansichten wurde entfernt.
                // Die Prüfung erfolgt nun pro Element in der Schleife.

                const scrollDistance = $(window).scrollTop();
                const windowHeight = $(window).height();
                const speedFactor = 0.7;

                parallaxElements.each((i, el) => {
                    const $el = $(el);

                    // NEUE PRÜFUNG: Parallax nur für dieses Element deaktivieren,
                    // wenn es mobil ist UND NICHT die Ausnahmeklasse besitzt.
                    if (isMobile && !$el.hasClass("parallax-mobile-enabled")) {
                        $el.css("transform", ""); // Transform zurücksetzen
                        this.parallaxState.delete(el);
                        return; // Zum nächsten Element in der Schleife springen
                    }

                    // Die restliche Parallax-Logik bleibt unverändert
                    const elementOffset = $el.offset().top;
                    const baseParallax = Math.round(scrollDistance - elementOffset + (windowHeight / 2));

                    let targetX = 0;
                    let targetY = 0;

                    if (typeof $el.data("parallax-y") !== 'undefined') {
                        const parallaxSpeedY = parseFloat($el.data("parallax-y")) * speedFactor;
                        targetY = baseParallax * -1 * parallaxSpeedY;
                    }
                    if (typeof $el.data("parallax-x") !== 'undefined') {
                        const parallaxSpeedX = parseFloat($el.data("parallax-x")) * speedFactor;
                        targetX = baseParallax * -1 * parallaxSpeedX;
                    }

                    let state = this.parallaxState.get(el);
                    if (!state) {
                        state = { currentX: targetX, currentY: targetY, targetX: targetX, targetY: targetY };
                        this.parallaxState.set(el, state);
                    } else {
                        state.targetX = targetX;
                        state.targetY = targetY;
                    }
                });
            },

            applyParallax: function() {
                const easingFactor = 0.1;
                const parallaxElements = $("[data-parallax-y], [data-parallax-x]").not(".marquee__line");

                parallaxElements.each((i, el) => {
                    const state = this.parallaxState.get(el);
                    if (!state) return;

                    state.currentX += (state.targetX - state.currentX) * easingFactor;
                    state.currentY += (state.targetY - state.currentY) * easingFactor;
                    
                    if (Math.abs(state.targetX - state.currentX) < 0.01) state.currentX = state.targetX;
                    if (Math.abs(state.targetY - state.currentY) < 0.01) state.currentY = state.targetY;

                    $(el).css("transform", `translate(${state.currentX.toFixed(2)}px, ${state.currentY.toFixed(2)}px)`);
                });
            },

            updateMarqueeScroll: function() {
                const $window = $(window);
                const viewPortStart = $window.scrollTop();
                const viewPortHeight = $window.height();
                const viewPortEnd = viewPortStart + viewPortHeight;

                $(".marquee").each(function() {
                    const $stage = $(this);
                    const $movers = $stage.find(".marquee__line[data-parallax-x]");
                    if (!$movers.length) return;

                    const stageTop = $stage.offset().top;
                    const stageHeight = $stage.outerHeight();
                    const stageBottom = stageTop + stageHeight;

                    const isVisible = stageBottom > viewPortStart && stageTop < viewPortEnd;
                    if (!isVisible) return;
                    
                    const animationStart = stageTop - viewPortHeight;
                    const animationEnd = stageBottom;
                    let progress = (viewPortStart - animationStart) / (animationEnd - animationStart);
                    progress = Math.min(Math.max(progress, 0), 1);

                    let maxTranslate;
                    if ($window.width() < 700) {
                        maxTranslate = $stage.outerWidth() * 3; 
                    } else {
                        maxTranslate = $stage.outerWidth() * 1.2;
                    }

                    $movers.each(function() {
                        const $mover = $(this);
                        const parallaxFactor = parseFloat($mover.data("parallax-x"));
                        const translateX = (progress - 0.3) * maxTranslate * parallaxFactor;
                        $mover.css({ transform: `translateX(${translateX.toFixed(2)}px)` });
                    });
                });
            }
        },

        //======================================================================
        // MODULE: VIDEO PLAYER
        //======================================================================
        video: {
            init: function() {
                $(".js-video-wrapper").each(function() {
                    const $wrapper = $(this);
                    const $video = $wrapper.find('.js-video');
                    const $image = $wrapper.find('.js-video-image');
                    const $cursor = $wrapper.find('.js-video-cursor');
                    const $youtube = $wrapper.find(".js-video-youtube");
                    const videoEl = $video.get(0);
                    $wrapper.on('mousemove', e => {
                        const wrapperOffset = $wrapper.offset();
                        const relativeX = e.pageX - wrapperOffset.left;
                        const relativeY = e.pageY - wrapperOffset.top;
                        $cursor.removeClass("transition").css({
                            'transform': 'none',
                            left: relativeX - $cursor.outerWidth() / 2,
                            top: relativeY - $cursor.outerHeight() / 2
                        });
                    });
                    $wrapper.on('mouseleave', () => {
                        $cursor.addClass("transition").css({
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)'
                        });
                    });
                    $wrapper.on('click', function(event) {
                        if ($youtube.length) {
                            $youtube.get(0).click();
                        } else {
                            event.preventDefault();
                            $wrapper.toggleClass("active");
                            if ($wrapper.hasClass("active")) {
                                $image.hide();
                                $cursor.hide();
                                $video.prop({ 'muted': false, 'controls': true });
                                $wrapper.css('cursor', 'auto');
                                videoEl.play();
                            } else {
                                $wrapper.css('cursor', 'none');
                                $cursor.show();
                                videoEl.pause();
                            }
                        }
                    });
                });
            }
        },

        //======================================================================
        // MODULE: PDF VIEWER (PDF.js)
        //======================================================================
        /**
         * @description Zeigt PDFs (.js-pdf-viewer-link) in einem eigenen Overlay. PDF.js rendert die Seiten
         * als Canvas, damit die Darstellung in allen Browsern (auch mobil) gleich aussieht.
         * PDF.js liegt unter js/vendor/ und wird erst beim ersten Öffnen nachgeladen.
         * Markup: Chunk pdfViewer (Pfad zu PDF.js im Attribut data-pdfjs).
         * Deep-Link #fahrzeug-ID: öffnet das PDF des Links mit data-car="ID". Steht die Karte (#fahrzeug-ID) auf der Seite,
         * wird sie nach dem Schließen gezeigt. Beim Öffnen eines Fahrzeug-PDFs steht der Deep-Link in der URL.
         */
        pdfViewer: {
            $el: null,
            library: null,
            worker: null,
            loadingTask: null,
            observer: null,
            session: 0,
            lastFocus: null,
            previousHash: '',
            $target: null,

            init: function() {
                const $viewers = $('.js-pdf-viewer');
                if (!$viewers.length) return;

                // an den body hängen, damit position: fixed nicht an transformierten Eltern (Parallax) hängt
                this.$el = $viewers.first().appendTo('body');
                $viewers.slice(1).remove();

                $(document).on('click', '.js-pdf-viewer-link', (e) => {
                    e.preventDefault();
                    const $link = $(e.currentTarget);
                    this.open(e.currentTarget.href, $link.data('title') || '', $link.data('car'));
                });
                this.$el.on('click', '.js-pdf-viewer-close', () => this.close());
                this.$el.find('.js-pdf-viewer-scroll').on('scroll', () => this.updateCount());
                $(document).on('keydown', (e) => {
                    if (e.key === 'Escape' && this.isOpen()) this.close();
                });

                let resizeTimer;
                $(window).on('resize', () => {
                    clearTimeout(resizeTimer);
                    resizeTimer = setTimeout(() => this.renderNearPages(), 200);
                });

                $(window).on('hashchange', () => this.openFromHash());
                this.openFromHash();
            },

            openFromHash: function() {
                const match = window.location.hash.match(/^#fahrzeug-(\d+)$/);
                if (!match) return;

                // die Karte in der Sales-Liste hat Vorrang vor anderen Links (z.B. Button im Hero Special)
                const $links = $('.js-pdf-viewer-link[data-car="' + match[1] + '"]');
                const $card = $links.filter('#fahrzeug-' + match[1]);
                const $link = $card.length ? $card : $links.first();
                if (!$link.length) return;

                this.open($link[0].href, $link.data('title') || '', match[1]);
                this.$target = $card.length ? $card : null;
            },

            // Karte nach dem Schließen zeigen, ausgefiltert erst nach dem Zurücksetzen der Filter (MixItUp-Animation)
            reveal: function($card) {
                const show = () => {
                    $card[0].scrollIntoView({ block: 'center' });
                    $card.removeClass('is-highlighted');
                    void $card[0].offsetWidth; // Reflow, damit die Animation erneut startet
                    $card.addClass('is-highlighted');
                };

                if (!$card.is(':hidden')) return show();
                $('.js-advanced-filter-module')
                    .filter((index, el) => $card.closest($(el).data('target')).length > 0)
                    .find('.js-filter-reset').trigger('click');
                setTimeout(show, 400);
            },

            isOpen: function() {
                return this.$el.hasClass('is-open');
            },

            loadLibrary: function() {
                if (!this.library) {
                    const base = this.$el.data('pdfjs');
                    this.library = import(base + 'pdf.min.js')
                        .then((pdfjs) => {
                            pdfjs.GlobalWorkerOptions.workerSrc = base + 'pdf.worker.min.js';
                            return pdfjs;
                        })
                        .catch((error) => {
                            this.library = null; // beim nächsten Öffnen erneut versuchen
                            throw error;
                        });
                }
                return this.library;
            },

            open: function(url, title, car) {
                // jede Öffnung bekommt eine Session, damit verspätete Antworten eines vorherigen PDFs ignoriert werden
                const session = ++this.session;
                const base = this.$el.data('pdfjs');

                // Deep-Link in die URL, damit sich das Fahrzeug teilen lässt (ohne neuen Eintrag im Verlauf)
                if (car) {
                    if (!/^#fahrzeug-/.test(window.location.hash)) this.previousHash = window.location.hash;
                    history.replaceState(history.state, '', '#fahrzeug-' + car);
                }

                this.$target = null;
                this.lastFocus = document.activeElement;
                this.$el.find('.js-pdf-viewer-title').text(title);
                this.$el.find('.js-pdf-viewer-download').attr('href', url);
                this.$el.find('.js-pdf-viewer-count').text('');
                this.$el.find('.js-pdf-viewer-pages').empty();
                this.setStatus('PDF wird geladen …');
                this.$el.attr({ 'aria-label': title || 'PDF', 'aria-hidden': 'false' }).addClass('is-open');
                $('body').addClass('freeze');
                this.$el.find('.js-pdf-viewer-scroll').scrollTop(0).trigger('focus');

                this.loadLibrary()
                    .then((pdfjs) => {
                        if (session !== this.session) return null;
                        // ein Worker für alle PDFs der Seite, spart das erneute Starten beim nächsten Öffnen
                        if (!this.worker || this.worker.destroyed) this.worker = new pdfjs.PDFWorker();
                        this.loadingTask = pdfjs.getDocument({
                            url: url,
                            worker: this.worker,
                            standardFontDataUrl: base + 'standard_fonts/',
                            wasmUrl: base + 'wasm/',
                            iccUrl: base + 'iccs/',
                        });
                        return this.loadingTask.promise;
                    })
                    .then((pdf) => {
                        if (pdf && session === this.session) return this.createPages(pdf, session);
                    })
                    .catch((error) => {
                        if (session !== this.session) return; // inzwischen geschlossen oder anderes PDF geöffnet
                        console.error(error);
                        this.setStatus('Das PDF konnte nicht angezeigt werden.', url);
                    });
            },

            createPages: function(pdf, session) {
                const requests = [];
                for (let number = 1; number <= pdf.numPages; number++) {
                    requests.push(pdf.getPage(number));
                }

                return Promise.all(requests).then((pages) => {
                    if (session !== this.session) return;
                    const $pages = this.$el.find('.js-pdf-viewer-pages');

                    // gerendert wird erst, wenn eine Seite in die Nähe des sichtbaren Bereichs kommt
                    this.observer = new IntersectionObserver((entries) => {
                        entries.forEach((entry) => {
                            $(entry.target).toggleClass('is-near', entry.isIntersecting);
                            if (entry.isIntersecting) this.renderPage($(entry.target));
                        });
                    }, { root: this.$el.find('.js-pdf-viewer-scroll')[0], rootMargin: '100% 0px' });

                    // Platzhalter im Seitenverhältnis des PDFs, damit beim Rendern nichts springt
                    pages.forEach((page) => {
                        const viewport = page.getViewport({ scale: 1 });
                        const $page = $('<div class="pdfViewer__page"></div>')
                            .css('aspect-ratio', viewport.width + ' / ' + viewport.height)
                            .data('page', page)
                            .appendTo($pages);
                        this.observer.observe($page[0]);
                    });

                    this.setStatus('');
                    this.updateCount();
                });
            },

            renderPage: function($page) {
                const page = $page.data('page');
                // Canvas-Auflösung passend zur angezeigten Breite, für Retina max. doppelt
                const width = Math.round($page.width() * Math.min(window.devicePixelRatio || 1, 2));
                if (!page || width <= ($page.data('renderedWidth') || 0)) return;

                const token = ($page.data('token') || 0) + 1;
                $page.data({ token: token, renderedWidth: width });

                const viewport = page.getViewport({ scale: width / page.getViewport({ scale: 1 }).width });
                const canvas = document.createElement('canvas');
                canvas.className = 'pdfViewer__canvas';
                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);

                page.render({ canvas: canvas, viewport: viewport }).promise
                    .then(() => {
                        if ($page.data('token') !== token) return; // inzwischen neu gerendert
                        // neues Canvas erst nach dem Rendern einsetzen, so flackert beim Neurendern nichts
                        $page.find('canvas').remove();
                        $page.append(canvas);
                        void canvas.offsetWidth; // Reflow, damit das erste Einblenden animiert
                        $page.addClass('is-rendered');
                    })
                    .catch((error) => {
                        if ($page.data('token') === token) $page.removeData('renderedWidth');
                        // Abbruch beim Schließen ist normal, alles andere melden
                        if (!error || error.name !== 'RenderingCancelledException') console.error(error);
                    });
            },

            renderNearPages: function() {
                if (!this.$el || !this.isOpen()) return;
                this.$el.find('.pdfViewer__page.is-near').each((index, el) => this.renderPage($(el)));
            },

            updateCount: function() {
                const $pages = this.$el.find('.pdfViewer__page');
                if ($pages.length < 2) return;

                // aktuelle Seite = letzte, deren Oberkante das obere Drittel erreicht hat
                const scroll = this.$el.find('.js-pdf-viewer-scroll')[0];
                const line = scroll.getBoundingClientRect().top + scroll.clientHeight / 3;
                let current = 1;
                $pages.each((index, el) => {
                    if (el.getBoundingClientRect().top <= line) current = index + 1;
                });
                this.$el.find('.js-pdf-viewer-count').text(current + ' / ' + $pages.length);
            },

            setStatus: function(text, fallbackUrl) {
                const $status = this.$el.find('.js-pdf-viewer-status').text(text);
                if (fallbackUrl) {
                    $status.append(' ', $('<a target="_blank" rel="noopener">In neuem Tab öffnen</a>').attr('href', fallbackUrl));
                }
            },

            close: function() {
                this.session++;
                if (this.loadingTask) {
                    this.loadingTask.destroy();
                    this.loadingTask = null;
                }
                if (this.observer) {
                    this.observer.disconnect();
                    this.observer = null;
                }
                this.$el.removeClass('is-open').attr('aria-hidden', 'true');
                $('body').removeClass('freeze');
                if (this.lastFocus) this.lastFocus.focus({ preventScroll: true });

                if (/^#fahrzeug-/.test(window.location.hash)) {
                    history.replaceState(history.state, '', window.location.pathname + window.location.search + this.previousHash);
                    this.previousHash = '';
                }
                if (this.$target) {
                    this.reveal(this.$target);
                    this.$target = null;
                }

                // Canvas-Speicher nach dem Ausblenden freigeben
                setTimeout(() => {
                    if (!this.isOpen()) this.$el.find('.js-pdf-viewer-pages').empty();
                }, 400);
            }
        },

        //======================================================================
        // MODULE: MERKLISTE (Sales-Fahrzeuge)
        //======================================================================
        /**
         * @description Merkliste ohne Anmeldung, die IDs liegen im localStorage (createStore).
         * - .js-favorite-toggle[data-car]: Bookmark auf der Karte (Chunk salesCarsTpl)
         * - .js-favorites-link / .js-favorites-count: Link zur Merkliste mit Anzahl (Chunk salesCarsFilter)
         * - .js-favorites-list: Merkliste (Chunk salesFavorites). Enthält alle Sales-Fahrzeuge,
         *   gezeigt werden nur die gemerkten (.is-favorite), in der Reihenfolge der Sales-Liste.
         * Ohne Speicher bleiben Bookmarks und Link ausgeblendet.
         */
        favorites: {
            store: null,
            $list: null,

            init: function() {
                this.$list = $('.js-favorites-list');
                if (!$('.js-favorite-toggle, .js-favorites-link').length && !this.$list.length) return;

                this.store = createStore('dog.sales.favorites');
                if (!this.store.available) {
                    this.$list.closest('.salesFavorites').find('.js-favorites-empty').prop('hidden', false);
                    return;
                }

                $('.js-favorite-toggle').prop('hidden', false);
                $('.js-favorites-link').filter((index, el) => el.getAttribute('href')).prop('hidden', false);

                $(document).on('click', '.js-favorite-toggle', (e) => {
                    e.preventDefault();
                    this.store.toggle($(e.currentTarget).data('car'));
                });
                $(document).on('click', '.js-favorites-link.is-disabled', (e) => e.preventDefault());
                $(document).on('click', '.js-favorites-clear', () => {
                    if (window.confirm('Alle Fahrzeuge von der Merkliste entfernen?')) this.store.clear();
                });
                $(document).on(this.store.event, () => this.update(true));

                // Merkliste und Vergleich enthalten alle Sales-Fahrzeuge, nicht mehr vorhandene IDs fliegen raus
                const $complete = $('.js-favorites-list, .js-compare-list').first();
                if ($complete.length) {
                    this.store.prune($complete.children('[data-car]').map((index, el) => $(el).data('car')).get());
                }
                this.update(false);
            },

            update: function(animate) {
                const ids = this.store.ids();

                $('.js-favorite-toggle').each((index, el) => {
                    const active = ids.includes(parseInt($(el).data('car'), 10));
                    $(el).toggleClass('is-active', active).attr({
                        'aria-pressed': active ? 'true' : 'false',
                        'aria-label': active ? 'Von der Merkliste entfernen' : 'Fahrzeug merken',
                    });
                });

                $('.js-favorites-count').text(ids.length);
                $('.js-favorites-link').toggleClass('is-disabled', !ids.length).attr({
                    'aria-disabled': ids.length ? 'false' : 'true',
                    title: ids.length ? '' : 'Noch keine Fahrzeuge gemerkt',
                });

                if (this.$list.length) this.updateList(ids, animate);
            },

            updateList: function(ids, animate) {
                const $module = this.$list.closest('.salesFavorites');

                this.$list.children('[data-car]').each((index, el) => {
                    const $item = $(el);
                    const active = ids.includes(parseInt($item.data('car'), 10));
                    if (active === $item.hasClass('is-favorite') && !$item.hasClass('is-removing')) return;

                    clearTimeout($item.data('removeTimer'));
                    if (active || !animate) {
                        $item.removeClass('is-removing').toggleClass('is-favorite', active);
                        return;
                    }
                    // entfernte Karte erst ausblenden, dann aus dem Grid nehmen
                    $item.addClass('is-removing');
                    $item.data('removeTimer', setTimeout(() => {
                        $item.removeClass('is-removing is-favorite');
                        this.updateEmpty($module, this.store.ids().length);
                    }, 300));
                });

                $module.find('.js-favorites-clear').toggleClass('is-hidden', !ids.length);
                if (!animate || ids.length) this.updateEmpty($module, ids.length);
            },

            updateEmpty: function($module, count) {
                $module.find('.js-favorites-empty').prop('hidden', count > 0);
                $module.find('.salesFavorites__bar').toggleClass('is-empty', count === 0);
            }
        },

        //======================================================================
        // MODULE: VERGLEICH (Sales-Fahrzeuge)
        //======================================================================
        /**
         * @description Vergleich von bis zu 3 Fahrzeugen der Merkliste, die IDs liegen im localStorage (createStore).
         * Verglichen wird nur, was auf der Merkliste steht: wer ein Fahrzeug von der Merkliste nimmt, nimmt es auch aus dem Vergleich.
         * - .js-compare-toggle: Checkbox „Vergleichen“ auf der Karte der Merkliste (Chunk salesCompareToggle)
         * - .js-compare-bar / .js-compare-count / .js-compare-link: Leiste mit Link zum Vergleich (Chunk salesFavorites)
         * - .js-compare-list: Vergleich (Chunk salesCompare). Enthält alle Sales-Fahrzeuge,
         *   gezeigt werden die ausgewählten (.is-compared) in der Reihenfolge der Auswahl, erst ab 2 Fahrzeugen.
         *   Zeilen ohne Wert bei allen gezeigten Fahrzeugen ([data-row] ohne data-filled) werden ausgeblendet.
         */
        compare: {
            store: null,
            $list: null,
            max: 3,

            init: function() {
                this.$list = $('.js-compare-list');
                if (!$('.js-compare-toggle').length && !this.$list.length) return;

                const favorites = Site.favorites.store;
                if (!favorites || !favorites.available) {
                    this.$list.prop('hidden', true).closest('.salesCompare').find('.js-compare-empty').prop('hidden', false);
                    return;
                }

                this.store = createStore('dog.sales.compare', { max: this.max });
                $('.js-compare-option').prop('hidden', false);

                $(document).on('change', '.js-compare-toggle', (e) => {
                    const input = e.currentTarget;
                    if (!input.checked) {
                        this.store.remove(input.value);
                    } else if (!this.store.add(input.value)) {
                        input.checked = false;
                    }
                });
                $(document).on('click', '.js-compare-clear', () => this.store.clear());
                $(document).on('click', '.js-compare-link.is-disabled', (e) => e.preventDefault());
                $(document).on(this.store.event, () => this.update());
                $(document).on(favorites.event, () => this.store.prune(favorites.ids()));

                this.store.prune(favorites.ids());
                this.update();
            },

            update: function() {
                const ids = this.store.ids();
                const full = ids.length >= this.max;

                $('.js-compare-toggle').each((index, el) => {
                    el.checked = ids.includes(parseInt(el.value, 10));
                    el.disabled = full && !el.checked;
                    $(el).closest('.js-compare-option')
                        .toggleClass('is-disabled', el.disabled)
                        .attr('title', el.disabled ? 'Es können maximal ' + this.max + ' Fahrzeuge verglichen werden' : null);
                });

                $('.js-compare-count').text(ids.length);
                $('.js-compare-bar').prop('hidden', !ids.length);
                $('.js-compare-link').toggleClass('is-disabled', ids.length < 2).attr({
                    'aria-disabled': ids.length < 2 ? 'true' : 'false',
                    title: ids.length < 2 ? 'Mindestens 2 Fahrzeuge auswählen' : null,
                });

                if (this.$list.length) this.updateList(ids);
            },

            updateList: function(ids) {
                const enough = ids.length >= 2;
                const $items = this.$list.children('[data-car]');

                $items.each((index, el) => {
                    const position = ids.indexOf(parseInt($(el).data('car'), 10));
                    $(el).toggleClass('is-compared', enough && position > -1).css('order', position);
                });

                // Zeile ausblenden, wenn keines der gezeigten Fahrzeuge einen Wert hat (z.B. 0–100 km/h)
                const filled = {};
                $items.filter('.is-compared').find('[data-row]').each((index, el) => {
                    const row = $(el).data('row');
                    filled[row] = filled[row] || el.hasAttribute('data-filled');
                });
                this.$list.find('[data-row]').each((index, el) => {
                    $(el).toggleClass('is-hidden', !filled[$(el).data('row')]);
                });

                const $module = this.$list.closest('.salesCompare');
                this.$list.prop('hidden', !enough);
                $module.find('.js-compare-empty').prop('hidden', enough);
                $module.find('.salesCompare__bar').toggleClass('is-empty', !enough);
            }
        },

        //======================================================================
        // MODULE: HELPERS
        //======================================================================
        helpers: {
            initExternalLinks: function() {
                $('a:not([data-targetblank=ignore])').each(function() {
                    if (this.hostname && this.hostname !== location.hostname) {
                        $(this).attr('target', '_blank');
                    }
                });
            }
        }
    };

    $(function() {
        Site.init();
    });

})(jQuery);